import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useAI } from "@/context/AIContext";
import { Button } from "@/components/ui/button";
import { CircuitBoard, Handshake, Store, Sparkles, ArrowRight } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const { openAI } = useAI();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/me/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const stats = data?.stats;

  return (
    <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8" data-testid="dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono-tech text-xs uppercase tracking-widest text-[var(--teal-deep)]">Workspace</p>
          <h1 className="mt-2 font-display text-3xl font-800 tracking-tight text-[#1C2A38]">
            Welcome, {user?.name?.split(" ")[0] || "Builder"}
          </h1>
          <p className="mt-2 text-[#5b6878]">{user?.company ? `${user.company} · ` : ""}{user?.email}</p>
        </div>
        <Button onClick={openAI} data-testid="dashboard-ai-btn" className="bg-[var(--teal)] font-600 text-[#063b34] hover:bg-[#1ee9cf]">
          <Sparkles className="mr-1.5 h-4 w-4" /> Ask ROSA AI
        </Button>
      </div>

      <div className="mt-8 grid gap-5 sm:grid-cols-3">
        {[
          { label: "Components", value: stats?.components, icon: CircuitBoard, prompt: "Show me popular robotics components and what to consider when choosing them." },
          { label: "Suppliers", value: stats?.suppliers, icon: Handshake, prompt: "Which verified suppliers do you recommend for robotics parts?" },
          { label: "Marketplace listings", value: stats?.listings, icon: Store, prompt: "What good deals are on the ROSA marketplace right now?" },
        ].map((c) => {
          const Icon = c.icon;
          return (
            <button key={c.label} onClick={() => openAI(c.prompt)} data-testid={`dash-card-${c.label.split(" ")[0].toLowerCase()}`}
              className="group rounded-2xl border border-[#1C2A38]/10 bg-[var(--surface)] p-6 text-left shadow-sm transition-all hover:-translate-y-1 hover:border-[#1C2A38]/20 hover:shadow-lg">
              <div className="flex items-center justify-between">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#EEF1F3]">
                  <Icon className="h-5 w-5 text-[#1C2A38]" />
                </span>
                <ArrowRight className="h-4 w-4 text-[#8593a3] transition-transform group-hover:translate-x-1" />
              </div>
              <div className="mt-5 font-display text-3xl font-800 text-[#1C2A38]">{c.value ?? "—"}</div>
              <div className="mt-1 text-sm text-[#5b6878]">{c.label}</div>
            </button>
          );
        })}
      </div>

      <div className="mt-10">
        <h2 className="font-display text-xl font-700 text-[#1C2A38]">Recently added components</h2>
        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(data?.recent_components || []).map((it) => (
            <div key={it.id} data-testid="dash-recent-item" className="flex items-center gap-4 rounded-2xl border border-[#1C2A38]/10 bg-[var(--surface)] p-4 shadow-sm">
              <img src={it.image} alt={it.name} className="h-14 w-14 shrink-0 rounded-lg object-cover" />
              <div className="min-w-0">
                <h3 className="truncate font-display text-sm font-700 text-[#1C2A38]">{it.name}</h3>
                <p className="truncate font-mono-tech text-xs text-[#5b6878]">{it.specs}</p>
                <p className="mt-1 text-sm font-700 text-[var(--teal-deep)]">${it.price.toFixed(2)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
